import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { JobType, MeterRateTaskType } from '../../redux/jobs/jobs.types';
import { ApplicationConfig, ExcelExportFeatures } from '../../redux/application/application.types';
import { ColumnSchema, getFreezeCount, getColIndex } from './ColumnSchemaBuilder';
import { BAND_GROUP_LABEL_KEYS, BAND_GROUP_FILLS } from './ColumnGroups';
import {
    buildStatusFormula,
    buildConditionalFormats,
    buildCellValidationFormats,
} from '../../helper/ExcelValidationHelper';
import { mergeJobConfig } from '../../helper/JobConfigHelper';
import { getAllowedReadingDateRange, toExcelDateValue } from '../../helper/ReadingDateRangeHelper';
import { METER_TYPES, shortenObis } from '../../helper/MeterHelper';
import { getLatestReadingEvent, getLatestReadingResult } from '../../helper/JobHelper';
import i18n from '../../i18n';
import { extractExportColumnValue, sanitizeCellText, resolveRowMeterType } from './ColumnValueResolver';

const ROW_BATCH_SIZE = 25;
const DATA_ROW_HEIGHT = 20;
// Layout: Zeile 1 = Gruppenband, Zeile 2 = Header (visible labels; hidden
// technical columns keep machine-readable keys so import can identify them),
// Daten ab Zeile 3. Spalte 1 ist die schmale Medium-Farbkante.
const GROUP_BAND_ROW = 1;
const HEADER_ROW = 2;
const FIRST_DATA_ROW = 3;
const MEDIUM_EDGE_COL = 1;
const MEDIUM_EDGE_WIDTH = 1.2;
const IMPORTANT_COLUMN_KEYS = new Set([
    'meter.meterNumber',
    'meterRateTask.obis',
    '__readingDate',
    '__readingValue',
    '__status',
]);

// Medium-Farbkante: die pastelligen Medium-Farben aus tailwind.config.js
// ('electric-meter' = red-300 usw.), damit App und Export dieselbe Farbwelt
// nutzen. Einzige Medium-Kennzeichnung im Blatt — die Datenzeilen selbst
// bleiben ungefüllt.
const MEDIUM_EDGE_COLORS: Record<METER_TYPES, string> = {
    [METER_TYPES.ELECTRIC_METER]: 'FFFCA5A5', // red-300
    [METER_TYPES.GAS_METER]: 'FFFDBA74',      // orange-300
    [METER_TYPES.WATER_METER]: 'FF93C5FD',    // blue-300
    [METER_TYPES.WATER_WARM_METER]: 'FF93C5FD',
    [METER_TYPES.HEAT_METER]: 'FFF9A8D4',     // pink-300
    [METER_TYPES.GENERIC_METER]: 'FFD1D5DB',  // gray-300
};

const INPUT_BORDER_COLOR = 'FF000000';

const yieldToBrowser = () =>
    new Promise<void>((resolve) => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                setTimeout(resolve, 0);
            });
            return;
        }

        setTimeout(resolve, 0);
    });

function columnIndexToLetter(index: number): string {
    let result = '';
    while (index > 0) {
        const remainder = (index - 1) % 26;
        result = String.fromCharCode(65 + remainder) + result;
        index = Math.floor((index - 1) / 26);
    }
    return result;
}

function compareText(a?: string, b?: string): number {
    return String(a ?? '').localeCompare(String(b ?? ''), 'de', { numeric: true, sensitivity: 'base' });
}

function buildCellFill(argb: string): ExcelJS.Fill {
    return {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb },
        // bgColor immer mitsetzen: Apple Numbers rendert solide Füllungen ohne
        // bgColor mit der Standard-Palette (grelle Fremdfarben, #55).
        bgColor: { argb },
    };
}

function buildInputBorder(): Partial<ExcelJS.Borders> {
    const side = { style: 'medium' as const, color: { argb: INPUT_BORDER_COLOR } };
    return {
        top: side,
        left: side,
        bottom: side,
        right: side,
    };
}

function getDisplayLength(value: ExcelJS.CellValue): number {
    if (value instanceof Date) return 10;
    if (typeof value === 'object' && value !== null && 'formula' in value) {
        return 18;
    }

    return String(value ?? '').length;
}

function extractAdditionalValue(key: string, job: JobType, task: MeterRateTaskType): string | number | Date | '' {
    const value = extractExportColumnValue(key, job, task);
    if (key === 'meterRateTask.meterType' && typeof value === 'string' && value) {
        return i18n.t(`common.metertypes.${value}`, { defaultValue: value }) as string;
    }
    return value;
}

// Zählwerks-Format: feste Vor- und Nachkommastellen, immer voll angezeigt
// (z.B. preDecimals 6, postDecimals 1 → '000000.0' → 45210 wird 045210,0).
// Nur wenn preDecimals bekannt ist, werden führende Nullen erzwungen.
function buildMeterValueNumFmt(preDecimals: number | undefined, postDecimals: number): string {
    const prePart = preDecimals && preDecimals > 0 ? '0'.repeat(preDecimals) : '0';
    return postDecimals > 0 ? `${prePart}.${'0'.repeat(postDecimals)}` : prePart;
}

export async function buildWorkbook(
    jobs: JobType[],
    schema: ColumnSchema[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _features: ExcelExportFeatures,
    applicationConfig?: ApplicationConfig,
): Promise<Blob> {
    const t = (key: string, opts?: { defaultValue?: string; count?: number }): string => i18n.t(key, opts) as string;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('management.excelExport.file.sheetName', { defaultValue: 'Ableseliste' }));

    // Step 2: Set column widths (sheet column 1 = medium color edge)
    worksheet.getColumn(MEDIUM_EDGE_COL).width = MEDIUM_EDGE_WIDTH;
    schema.forEach(col => {
        worksheet.getColumn(col.colIndex!).width = col.width ?? 12;
        worksheet.getColumn(col.colIndex!).hidden = col.zone === 'hidden';
    });

    // Zeile 1: Gruppenband — zusammenhängende sichtbare Spalten derselben
    // Gruppe werden gemergt und dezent eingefärbt. Versteckte Systemspalten
    // bekommen kein Band.
    const bandRow = worksheet.getRow(GROUP_BAND_ROW);
    bandRow.height = 18;
    const visibleCols = schema.filter(col => col.zone !== 'hidden');
    let bandStart = 0;
    while (bandStart < visibleCols.length) {
        let bandEnd = bandStart;
        while (
            bandEnd + 1 < visibleCols.length &&
            visibleCols[bandEnd + 1].group === visibleCols[bandStart].group &&
            visibleCols[bandEnd + 1].colIndex === visibleCols[bandEnd].colIndex! + 1
        ) {
            bandEnd++;
        }
        const group = visibleCols[bandStart].group;
        const fillColor = group ? BAND_GROUP_FILLS[group] : undefined;
        const labelKey = group ? BAND_GROUP_LABEL_KEYS[group] : undefined;
        if (bandEnd > bandStart) {
            worksheet.mergeCells(GROUP_BAND_ROW, visibleCols[bandStart].colIndex!, GROUP_BAND_ROW, visibleCols[bandEnd].colIndex!);
        }
        for (let i = bandStart; i <= bandEnd; i++) {
            const cell = bandRow.getCell(visibleCols[i].colIndex!);
            if (fillColor) cell.fill = buildCellFill(fillColor);
            cell.protection = { locked: true };
        }
        const firstCell = bandRow.getCell(visibleCols[bandStart].colIndex!);
        if (labelKey) {
            firstCell.value = t(labelKey, { defaultValue: group });
            firstCell.font = { bold: true };
            firstCell.alignment = { vertical: 'middle', horizontal: 'left' };
        }
        bandStart = bandEnd + 1;
    }

    // Zeile 2: Header — sichtbare Spalten mit Label und Gruppenfarbe;
    // versteckte Systemspalten behalten ihre maschinenlesbaren Keys, damit der
    // Import sie unabhängig von Umbenennungen identifizieren kann.
    const headerRow = worksheet.getRow(HEADER_ROW);
    headerRow.height = 22;
    schema.forEach(col => {
        const cell = headerRow.getCell(col.colIndex!);
        cell.value = col.zone === 'hidden' ? col.key : col.label;
        cell.font = { bold: true };
        if (col.zone !== 'hidden' && col.group && BAND_GROUP_FILLS[col.group]) {
            cell.fill = buildCellFill(BAND_GROUP_FILLS[col.group]);
        }
        cell.alignment = { vertical: 'middle' };
        cell.protection = { locked: true };
    });

    // Die Farbkanten-Spalte läuft in beiden Kopfzeilen in der Farbe der ersten
    // Gruppe mit, damit die Ecke nicht weiß bleibt.
    const firstGroupFill = visibleCols[0]?.group ? BAND_GROUP_FILLS[visibleCols[0].group!] : undefined;
    if (firstGroupFill) {
        bandRow.getCell(MEDIUM_EDGE_COL).fill = buildCellFill(firstGroupFill);
        headerRow.getCell(MEDIUM_EDGE_COL).fill = buildCellFill(firstGroupFill);
    }
    bandRow.getCell(MEDIUM_EDGE_COL).protection = { locked: true };
    headerRow.getCell(MEDIUM_EDGE_COL).protection = { locked: true };

    // Step 3: Freeze panes (Farbkante + frozen zone, beide Kopfzeilen)
    const freezeCount = getFreezeCount(schema);
    worksheet.views = [{
        state: 'frozen',
        xSplit: freezeCount + 1,
        ySplit: HEADER_ROW,
        topLeftCell: `${columnIndexToLetter(freezeCount + 2)}${FIRST_DATA_ROW}`,
    }];

    // Step 4: Build row data
    let originalRowIndex = 0;
    const rows = jobs
        .flatMap((job) =>
            job.meter.meterRateTasks.map((task) => ({
                job,
                task,
                originalIndex: originalRowIndex++,
                meterType: resolveRowMeterType(job, task),
            }))
        )
        .sort((a, b) => {
            // Die vom Aufrufer gelieferte Reihenfolge bleibt vollständig
            // erhalten, damit die in der Auftragsauswahl gewählte Sortierung
            // (z.B. nach Stadt) auch über Medien hinweg in der Datei ankommt
            // (#59). Das Medium zeigt allein die Farbkante; nur Tarife
            // desselben Zählers werden nach OBIS geordnet.
            if (a.job === b.job) {
                const obisCompare = compareText(a.task.obis, b.task.obis);
                if (obisCompare !== 0) return obisCompare;
            }

            return a.originalIndex - b.originalIndex;
        });

    if (rows.length === 0) {
        throw new Error('buildWorkbook called with no rows — export aborted');
    }

    // Track max content length for auto-sized visible columns. Configured widths are minimums.
    const contentLens = new Map<number, number>();
    schema.forEach(col => {
        if (col.zone !== 'hidden' && col.colIndex !== undefined) {
            contentLens.set(col.colIndex, col.label.length);
        }
    });

    // Step 5: For each data row
    await yieldToBrowser();
    for (let i = 0; i < rows.length; i++) {
        if (i > 0 && i % ROW_BATCH_SIZE === 0) {
            await yieldToBrowser();
        }

        const { job, task, meterType } = rows[i];
        const rowIndex = i + FIRST_DATA_ROW;

        const cellRef = (key: string): string => {
            const idx = getColIndex(schema, key);
            return idx ? `${columnIndexToLetter(idx)}${rowIndex}` : '';
        };
        const absColRef = (key: string): string => {
            const idx = getColIndex(schema, key);
            return idx ? `$${columnIndexToLetter(idx)}${rowIndex}` : '';
        };

        const latestEvent = getLatestReadingEvent(task);
        const latestResult = getLatestReadingResult(task);
        const alreadyRead = latestResult?.readingDate != null && latestResult.readingValue != null;

        // Per-job config resolution (analog JobDetails.tsx:313–338)
        const jobConfig = mergeJobConfig(job.config, applicationConfig);
        // Reading-date window is anchored on the job's execution window
        // (± configured flags), never on targetReadingDate or the export day.
        // toExcelDateValue keeps the bounds at true midnight serials so Excel's
        // own date-validation formula accepts hand-typed dates.
        const allowedRange = getAllowedReadingDateRange(job.jobConstraints, jobConfig);
        const minAllowedDate = toExcelDateValue(allowedRange.minAllowedDate);
        const maxAllowedDate = toExcelDateValue(allowedRange.maxAllowedDate);
        const effectivePostDecimals = jobConfig?.meterReading?.queryPostDecimals
            ? (task.postDecimals ?? 0)
            : 0;
        // Zählwerks-Format: Vor- und Nachkommastellen des Tasks werden immer
        // voll angezeigt (führende Nullen wie auf dem Zählwerk). Kosmetisch —
        // Validierung/Import sind unberührt.
        const valueNumFmt = buildMeterValueNumFmt(task.preDecimals, effectivePostDecimals);

        // Per-row validation flags (faithful an JobDetails). Historic/Continuous nur wenn Flag
        // gesetzt UND die jeweilige Vergleichsspalte im Format vorhanden ist.
        const hasLastReadingCol = getColIndex(schema, 'meterRateTask.lastReadingValue') !== undefined;
        const hasLastResultCol = getColIndex(schema, 'meterRateTask.lastResultValue') !== undefined;
        const isStrict = task.readingValueValidation?.strictValidation === true;
        const decimalsQueried = jobConfig?.meterReading?.queryPostDecimals === true;
        const histOn = jobConfig?.features?.historicReadingsValidationRequired === true && hasLastReadingCol;
        const contOn = jobConfig?.features?.continuousReadingsValidationRequired === true && hasLastResultCol;

        const fixedValueMap: Record<string, ExcelJS.CellValue> = {
            'meter.meterNumber': sanitizeCellText(job.meter.meterNumber ?? ''),
            // Gekürzte OBIS (analog App-Anzeige); der Import matcht über die
            // versteckten IDs bzw. akzeptiert die Kurzform beim Fallback-Matching.
            'meterRateTask.obis': sanitizeCellText(shortenObis(task.obis)),
            '__readingHint': '',
            'meterRateTask.targetReadingDate': task.targetReadingDate ? dayjs(task.targetReadingDate).toDate() : '',
            'meterRateTask.lastReadingValue': latestEvent?.readingValue ?? '',
            'meterRateTask.lastReadingDate': latestEvent?.readingDate ? dayjs(latestEvent.readingDate).toDate() : '',
            'meterRateTask.lastResultValue': latestResult?.readingValue ?? '',
            'meterRateTask.lastResultDate': latestResult?.readingDate ? dayjs(latestResult.readingDate).toDate() : '',
            '__jobId': job.id,
            '__taskId': task.taskId,
            '__minValue': task.readingValueValidation?.minimumValue ?? '',
            '__maxValue': task.readingValueValidation?.maximumValue ?? '',
            '__postDecimals': effectivePostDecimals,
            '__preDecimals': task.preDecimals ?? '',
            '__strictValidation': isStrict,
            '__noValidation': task.readingValueValidation == null,
            '__decimalsCheck': decimalsQueried,
            '__histCheck': histOn,
            '__contCheck': contOn,
            '__alreadyRead': alreadyRead,
            '__minAllowedDate': minAllowedDate,
            '__maxAllowedDate': maxAllowedDate,
        };

        const row = worksheet.getRow(rowIndex);
        row.height = DATA_ROW_HEIGHT;

        // Medium-Farbkante — einzige Medium-Kennzeichnung der Zeile
        const edgeCell = row.getCell(MEDIUM_EDGE_COL);
        edgeCell.fill = buildCellFill(MEDIUM_EDGE_COLORS[meterType]);
        edgeCell.protection = { locked: true };

        schema.forEach(col => {
            const cell = row.getCell(col.colIndex!);

            if (col.key === '__status') {
                // Status formula is set after this block
            } else if (col.key === '__readingDate') {
                cell.numFmt = 'DD.MM.YYYY';
                cell.border = buildInputBorder();
                // No initial value — leave empty so the cell type is not forced to string
            } else if (col.key === '__readingValue') {
                cell.numFmt = valueNumFmt;
                cell.border = buildInputBorder();
                // No initial value — leave empty so the cell type is not forced to string
            } else if (col.key in fixedValueMap) {
                cell.value = fixedValueMap[col.key];
                if (col.key === '__minAllowedDate' || col.key === '__maxAllowedDate') {
                    cell.numFmt = 'DD.MM.YYYY';
                } else if (
                    col.key === 'meterRateTask.lastReadingValue' ||
                    col.key === 'meterRateTask.lastResultValue' ||
                    col.key === '__minValue' ||
                    col.key === '__maxValue'
                ) {
                    cell.numFmt = valueNumFmt;
                }
            } else {
                cell.value = extractAdditionalValue(col.key, job, task);
            }

            if (col.key === '__readingHint') {
                cell.border = buildInputBorder();
            }

            if (IMPORTANT_COLUMN_KEYS.has(col.key)) {
                cell.font = { ...(cell.font ?? {}), bold: true };
            }
            cell.protection = { locked: col.locked };

            if (col.zone !== 'hidden' && col.colIndex !== undefined) {
                const displayLen = getDisplayLength(cell.value);
                const prev = contentLens.get(col.colIndex) ?? 0;
                if (displayLen > prev) contentLens.set(col.colIndex, displayLen);
            }
        });

        // c) Set status formula
        const statusColIdx = getColIndex(schema, '__status');
        if (statusColIdx) {
            const statusCell = worksheet.getRow(rowIndex).getCell(statusColIdx);
            statusCell.value = {
                formula: buildStatusFormula({
                    valueColRef: cellRef('__readingValue'),
                    dateColRef: cellRef('__readingDate'),
                    minColRef: absColRef('__minValue'),
                    maxColRef: absColRef('__maxValue'),
                    noValidationColRef: absColRef('__noValidation'),
                    postDecimalsColRef: absColRef('__postDecimals'),
                    preDecimalsColRef: absColRef('__preDecimals') || undefined,
                    strictColRef: absColRef('__strictValidation'),
                    decimalsCheckColRef: absColRef('__decimalsCheck'),
                    histCheckColRef: absColRef('__histCheck'),
                    contCheckColRef: absColRef('__contCheck'),
                    alreadyReadColRef: absColRef('__alreadyRead'),
                    lastReadingColRef: cellRef('meterRateTask.lastReadingValue'),
                    lastResultColRef: cellRef('meterRateTask.lastResultValue'),
                    minAllowedColRef: absColRef('__minAllowedDate') || undefined,
                    maxAllowedColRef: absColRef('__maxAllowedDate') || undefined,
                }, t),
            };
            statusCell.protection = { locked: true };
            statusCell.font = { ...(statusCell.font ?? {}), bold: true };

            const statusSchema = schema.find(col => col.key === '__status');
            if (statusSchema?.colIndex) {
                const prev = contentLens.get(statusSchema.colIndex) ?? 0;
                contentLens.set(statusSchema.colIndex, Math.max(prev, getDisplayLength(statusCell.value)));
            }
        }

        // e) Bewusst KEINE Excel-Datenüberprüfung auf Wert/Datum: Excel würde
        // dafür bei jeder Eingabe ein Popup zeigen bzw. die Eingabe ganz
        // verweigern, was die Datei unbenutzbar macht. Rückmeldung erfolgt
        // ausschließlich passiv über die Status-Formel (Text) und die
        // bedingten Formatierungen in Schritt 6. Die eigentliche Prüfung
        // passiert ohnehin beim Import.
    }

    // Step 5b: Auto-size visible columns while respecting configured minimum widths
    contentLens.forEach((maxLen, colIdx) => {
        const schemaCol = schema.find(col => col.colIndex === colIdx);
        const minWidth = schemaCol?.width ?? 8;
        worksheet.getColumn(colIdx).width = Math.max(minWidth, Math.min(50, maxLen + 2));
    });

    // Step 6: Conditional Formatting
    const lastRow = rows.length + FIRST_DATA_ROW - 1;
    const statusIdx = getColIndex(schema, '__status');
    const valueIdx2 = getColIndex(schema, '__readingValue');
    const dateIdx2 = getColIndex(schema, '__readingDate');
    const minIdx2 = getColIndex(schema, '__minValue');
    const maxIdx2 = getColIndex(schema, '__maxValue');
    const postDecIdx2 = getColIndex(schema, '__postDecimals');
    const preDecIdx2 = getColIndex(schema, '__preDecimals');
    const noValIdx2 = getColIndex(schema, '__noValidation');
    const minAllowedIdx2 = getColIndex(schema, '__minAllowedDate');
    const maxAllowedIdx2 = getColIndex(schema, '__maxAllowedDate');
    const decimalsCheckIdx2 = getColIndex(schema, '__decimalsCheck');
    const histCheckIdx2 = getColIndex(schema, '__histCheck');
    const contCheckIdx2 = getColIndex(schema, '__contCheck');
    const lastReadingIdx2 = getColIndex(schema, 'meterRateTask.lastReadingValue');
    const lastResultIdx2 = getColIndex(schema, 'meterRateTask.lastResultValue');

    if (statusIdx && valueIdx2 && dateIdx2) {
        const statusFormats = buildConditionalFormats({
            statusRef: `${columnIndexToLetter(statusIdx)}${FIRST_DATA_ROW}:${columnIndexToLetter(statusIdx)}${lastRow}`,
            valueRef: `${columnIndexToLetter(valueIdx2)}${FIRST_DATA_ROW}:${columnIndexToLetter(valueIdx2)}${lastRow}`,
            dateRef: `${columnIndexToLetter(dateIdx2)}${FIRST_DATA_ROW}:${columnIndexToLetter(dateIdx2)}${lastRow}`,
        }, t);

        statusFormats.forEach(fmt => {
            worksheet.addConditionalFormatting({ ref: fmt.ref, rules: fmt.rules });
        });
    }

    if (valueIdx2 && dateIdx2 && minIdx2 && maxIdx2 && postDecIdx2 && noValIdx2 && minAllowedIdx2 && maxAllowedIdx2
        && decimalsCheckIdx2 && histCheckIdx2 && contCheckIdx2) {
        const cellFormats = buildCellValidationFormats({
            valueRange: `${columnIndexToLetter(valueIdx2)}${FIRST_DATA_ROW}:${columnIndexToLetter(valueIdx2)}${lastRow}`,
            dateRange: `${columnIndexToLetter(dateIdx2)}${FIRST_DATA_ROW}:${columnIndexToLetter(dateIdx2)}${lastRow}`,
            valueFirstRef: `${columnIndexToLetter(valueIdx2)}${FIRST_DATA_ROW}`,
            dateFirstRef: `${columnIndexToLetter(dateIdx2)}${FIRST_DATA_ROW}`,
            minRef: `$${columnIndexToLetter(minIdx2)}${FIRST_DATA_ROW}`,
            maxRef: `$${columnIndexToLetter(maxIdx2)}${FIRST_DATA_ROW}`,
            postDecimalsRef: `$${columnIndexToLetter(postDecIdx2)}${FIRST_DATA_ROW}`,
            preDecimalsRef: preDecIdx2 ? `$${columnIndexToLetter(preDecIdx2)}${FIRST_DATA_ROW}` : undefined,
            noValidationRef: `$${columnIndexToLetter(noValIdx2)}${FIRST_DATA_ROW}`,
            decimalsCheckRef: `$${columnIndexToLetter(decimalsCheckIdx2)}${FIRST_DATA_ROW}`,
            histCheckRef: `$${columnIndexToLetter(histCheckIdx2)}${FIRST_DATA_ROW}`,
            contCheckRef: `$${columnIndexToLetter(contCheckIdx2)}${FIRST_DATA_ROW}`,
            lastReadingRef: lastReadingIdx2 ? `${columnIndexToLetter(lastReadingIdx2)}${FIRST_DATA_ROW}` : undefined,
            lastResultRef: lastResultIdx2 ? `${columnIndexToLetter(lastResultIdx2)}${FIRST_DATA_ROW}` : undefined,
            minAllowedRef: `$${columnIndexToLetter(minAllowedIdx2)}${FIRST_DATA_ROW}`,
            maxAllowedRef: `$${columnIndexToLetter(maxAllowedIdx2)}${FIRST_DATA_ROW}`,
        });

        cellFormats.forEach(fmt => {
            worksheet.addConditionalFormatting({ ref: fmt.ref, rules: fmt.rules });
        });
    }

    // AutoFilter über die sichtbaren Spalten der Header-Zeile
    const lastVisibleCol = visibleCols[visibleCols.length - 1];
    if (lastVisibleCol?.colIndex) {
        worksheet.autoFilter = {
            from: { row: HEADER_ROW, column: 2 },
            to: { row: HEADER_ROW, column: lastVisibleCol.colIndex },
        };
    }

    await yieldToBrowser();

    // Step 7: Worksheet protection (AutoFilter bleibt trotz Schutz benutzbar)
    await worksheet.protect('ExportV1!', {
        selectLockedCells: true,
        selectUnlockedCells: true,
        autoFilter: true,
        insertRows: false,
        deleteRows: false,
    });

    await yieldToBrowser();

    // Step 8: Return Blob
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
